import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send, User, MessageSquare, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const WHATSAPP_NUMBER = "447738998165";

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `Hi Pastor, my name is ${form.name}.\n\nSubject: ${form.subject || "General Enquiry"}\n\n${form.message}\n\nEmail: ${form.email}`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 px-6 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-navy/3 blur-3xl" />
      <div className="max-w-7xl mx-auto relative">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-navy/5 text-navy text-xs font-body font-bold uppercase tracking-[0.2em] mb-6">
            <Mail className="w-3.5 h-3.5" />
            Contact Us
          </span>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-navy mb-4">
            Get in Touch
          </h2>
          <p className="font-body text-slate-600 text-base max-w-lg mx-auto">
            Have a question or want to know more? Send us a message and we'll get back to you.
          </p>
        </motion.div>

        <motion.div
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 space-y-6"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-body font-semibold text-navy flex items-center gap-2">
                  <User className="w-4 h-4" /> Name
                </Label>
                <Input
                  data-testid="contact-name-input"
                  required
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-xl border-slate-200 focus:ring-navy font-body"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body font-semibold text-navy flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email
                </Label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-xl border-slate-200 focus:ring-navy font-body"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-body font-semibold text-navy flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Subject
              </Label>
              <Input
                data-testid="contact-subject-input"
                placeholder="What's this about?"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="rounded-xl border-slate-200 focus:ring-navy font-body"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-body font-semibold text-navy flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Message
              </Label>
              <Textarea
                data-testid="contact-message-input"
                required
                rows={5}
                placeholder="Your message..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="rounded-xl border-slate-200 focus:ring-navy font-body resize-none"
              />
            </div>
            <Button
              type="submit"
              data-testid="contact-submit-btn"
              className="w-full rounded-full bg-cgreen hover:bg-cgreen/90 text-white py-6 font-body font-bold text-base shadow-lg shadow-cgreen/30 transition-all hover:-translate-y-0.5"
            >
              <Send className="w-4 h-4 mr-2" /> Send via WhatsApp
            </Button>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <a href="tel:07738998165" className="flex items-center gap-2 text-navy font-body text-sm hover:underline">
                <Phone className="w-4 h-4" /> Call: 07738998165
              </a>
              <a href="mailto:dominionparish0@gmail.com" className="flex items-center gap-2 text-navy font-body text-sm hover:underline">
                <Mail className="w-4 h-4" /> dominionparish0@gmail.com
              </a>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
